import type { Variants } from '@amplitude/experiment-js-client';

import { AmplitudeExperiments, AmplitudeVariants } from '@/utils/amplitude/experiments/AmplitudeExperiments';

export const isCookiebotUiRedesignTreatment = (variants: Variants): boolean =>
    variants?.[AmplitudeExperiments.CookiebotUiRedesign]?.key === AmplitudeVariants.CookiebotUiRedesign.Treatment;

// EU + EEA + UK (UKGDPR) + broader Europe — used with CloudFront-Viewer-Country (ISO 3166-1 alpha-2)
const GDPR_COUNTRY_CODES = new Set([
    // Eastern Europe
    'BY', // Belarus
    'BG', // Bulgaria
    'CZ', // Czech Republic
    'HU', // Hungary
    'MD', // Moldova
    'PL', // Poland
    'RO', // Romania
    'RU', // Russia
    'SK', // Slovakia
    'UA', // Ukraine

    // Northern Europe
    'AX', // Åland Islands
    'DK', // Denmark
    'EE', // Estonia
    'FO', // Faeroe Islands
    'FI', // Finland
    'GG', // Guernsey
    'IS', // Iceland
    'IE', // Ireland
    'IM', // Isle of Man
    'JE', // Jersey
    'LV', // Latvia
    'LT', // Lithuania
    'NO', // Norway
    'SJ', // Svalbard and Jan Mayen
    'SE', // Sweden
    'GB', // United Kingdom

    // Southern Europe
    'AL', // Albania
    'AD', // Andorra
    'BA', // Bosnia and Herzegovina
    'HR', // Croatia
    'CY', // Cyprus
    'MK', // North Macedonia
    'GI', // Gibraltar
    'GR', // Greece
    'IT', // Italy
    'MT', // Malta
    'ME', // Montenegro
    'PT', // Portugal
    'SM', // San Marino
    'RS', // Serbia
    'SI', // Slovenia
    'ES', // Spain
    'VA', // Vatican City

    // Western Europe
    'AT', // Austria
    'BE', // Belgium
    'FR', // France
    'DE', // Germany
    'LI', // Liechtenstein
    'LU', // Luxembourg
    'MC', // Monaco
    'NL', // Netherlands
    'CH', // Switzerland
]);

export type CookiebotConsentUiFlags = {
    isGdprRegion: boolean;
    isUs: boolean;
    shouldLoadCookiebot: boolean;
    shouldSuppressNativeCookiebotDialog: boolean;
};

/**
 * SSR and client must use the same inputs: CloudFront country (or '') + experiment treatment flag.
 */
export const getCookiebotConsentUiFlags = (viewerCountry: string, isTreatment: boolean): CookiebotConsentUiFlags => {
    const trimmedViewerCountry = viewerCountry.trim();
    const viewerCountryCode = trimmedViewerCountry.toUpperCase();
    const isUsCode = viewerCountryCode === 'US';
    //The General Data Protection Regulation
    const isGdprRegion = GDPR_COUNTRY_CODES.has(viewerCountryCode);

    const shouldLoadCookiebot = (isTreatment && (isGdprRegion || isUsCode)) || (!isTreatment && isGdprRegion);

    const shouldSuppressNativeCookiebotDialog = isTreatment && (isGdprRegion || isUsCode);

    return {
        isGdprRegion,
        isUs: isUsCode,
        shouldLoadCookiebot,
        shouldSuppressNativeCookiebotDialog,
    };
};
